import { io } from "socket.io-client";
import { isDevelopment } from "../utils/settings";
import { getGlobalDispatch, getGlobalState } from "../State/Context";
import { ActionType } from "../State/State";
import { showSystemMessage } from "../api/api";
import { imgFromCoords } from "../utils/settings";
import { requestHexagenInfo } from "../api/hexagen";
import {
  getRegenerationFinishedActions,
  RegenerationFinishedPayload,
} from "./regenerationEvents";

const backendURL = isDevelopment
  ? "http://localhost:3000"
  : "https://hexagen.world";

export const socket = io(backendURL, {
  path: "/socket.io",
  autoConnect: false,
  withCredentials: true,
  transports: ["websocket", "polling"],
});

socket.on("connect", () => {
  console.log(
    "Connected to server, transport:",
    socket.io.engine.transport.name
  );
});

socket.on("connect_error", (error: any) => {
  console.log("connect_error", error.type, error.message);
});

socket.on("disconnect", (reason: any) => {
  console.log("disconnect", reason);
  if (reason === "io server disconnect") {
    console.log("Server disconnected the socket");
  }
});

export const initSocket = () => {
  socket.connect();
};

export const disconnectSocket = () => {
  if (socket.connected) {
    socket.disconnect();
  }
};

//
// REGENERATION STARTED
//
socket.on("regeneration started", (coordinates: { x: number; y: number }) => {
  getGlobalDispatch()({
    type: ActionType.ADD_REGENERATING_NOW_HEX,
    x: coordinates.x,
    y: coordinates.y,
  });
});

//
// REGENERATION FINISHED
//
socket.on("regeneration finished", (payload: RegenerationFinishedPayload) => {
  const dispatch = getGlobalDispatch();
  getRegenerationFinishedActions(payload).forEach(dispatch);
});

//
// GENERATION STARTED
//
socket.on(
  "generation started",
  (data: { x: number; y: number; playerUID: string }) => {
    console.log("generation started", data);
    getGlobalDispatch()({
      type: ActionType.ADD_GENERATING_NOW_HEX,
      x: data.x,
      y: data.y,
    });

    const playerUID = getGlobalState().player?.uid;
    if (playerUID !== data.playerUID) return;

    showSystemMessage({
      systemMessage: "success",
      text: "Generation has started.",
    });
  }
);

//
// HEX UPVOTED
//
socket.on(
  "hex upvoted",
  (data: { x: number; y: number; voterUID: string; voterName?: string }) => {
    const currentUid = getGlobalState().player?.uid;
    if (currentUid === data.voterUID) return;
    const text = `Player ${data.voterName || data.voterUID} upvoted a hexagon at (${data.x}, ${data.y})`;
    showSystemMessage({
      systemMessage: "success",
      text,
      imageUrl: imgFromCoords(data.x, data.y),
      navigateTo: { x: data.x, y: data.y },
    });
  }
);
socket.on(
  "video generation started",
  (data: { x: number; y: number; playerUID: string }) => {
    getGlobalDispatch()({
      type: ActionType.ADD_VIDEO_GENERATING_NOW_HEX,
      x: data.x,
      y: data.y,
    });

    const playerUID = getGlobalState().player?.uid;
    if (playerUID !== data.playerUID) return;

    showSystemMessage({
      systemMessage: "success",
      text: "Video generation has started.",
    });
  }
);
socket.on("video generation finished", async (coordinates: { x: number; y: number }) => {
  getGlobalDispatch()({
    type: ActionType.REMOVE_VIDEO_GENERATING_NOW_HEX,
    x: coordinates.x,
    y: coordinates.y,
  });

  const list = getGlobalState().hasVideo || [];
  const exists = list.some((h) => h.x === coordinates.x && h.y === coordinates.y);
  const updated = exists ? list : [...list, { x: coordinates.x, y: coordinates.y }];
  getGlobalDispatch()({
    type: ActionType.SET_HAS_VIDEO_LIST,
    list: updated,
  });

  await requestHexagenInfo(coordinates.x, coordinates.y);

  const state = getGlobalState();
  if (state.isHexInfoPopupOpen) {
    getGlobalDispatch()({ type: ActionType.CLOSE_HEX_INFO_POPUP });
  }
  getGlobalDispatch()({ type: ActionType.SELECT_HEX, x: coordinates.x, y: coordinates.y });
  requestAnimationFrame(() => {
    getGlobalDispatch()({ type: ActionType.OPEN_HEX_INFO_POPUP });
  });

  showSystemMessage({
    systemMessage: "success",
    text: `Video is ready at (${coordinates.x}, ${coordinates.y})`,
    imageUrl: imgFromCoords(coordinates.x, coordinates.y),
    navigateTo: { x: coordinates.x, y: coordinates.y },
  });
});
socket.on(
  "video generation failed",
  (data: { x: number; y: number; error?: string }) => {
    getGlobalDispatch()({
      type: ActionType.REMOVE_VIDEO_GENERATING_NOW_HEX,
      x: data.x,
      y: data.y,
    });

    showSystemMessage({
      systemMessage: "error",
      text: data.error || "Video generation failed",
    });
  }
);

//
// PLAYER JOINED
//
socket.on(
  "player joined",
  (data: { uid: string; name: string }) => {
    const currentUid = getGlobalState().player?.uid;
    if (currentUid === data.uid) return;
    showSystemMessage({
      systemMessage: "success",
      text: `Player ${data.name} joined the game`,
      navigateToPlayerUid: data.uid,
      playerName: data.name,
    });
  }
);

//
// GENERATION FINISHED
//
socket.on(
  "generation finished",
  (data: { x: number; y: number; playerUID: string; creatorName?: string }) => {
    console.log("generation finished", data);
    getGlobalDispatch()({
      type: ActionType.ADD_GENERATED_HEXAGON,
      x: data.x,
      y: data.y,
    });
    getGlobalDispatch()({
      type: ActionType.REMOVE_GENERATING_NOW_HEX,
      x: data.x,
      y: data.y,
    });

    const text = `Player ${data.creatorName || data.playerUID} created a new hexagon at (${data.x}, ${data.y})`;
    showSystemMessage({
      systemMessage: "success",
      text,
      imageUrl: imgFromCoords(data.x, data.y),
      navigateTo: { x: data.x, y: data.y },
    });

    const playerUID = getGlobalState().player?.uid;
    if (playerUID !== data.playerUID) return;

    const state = getGlobalState();
    const isOpen = state.isGeneratedAwardPopupOpen;
    const hasQueue = state.awardPopupQueue && state.awardPopupQueue.length > 0;
    if (!isOpen && !hasQueue) {
      getGlobalDispatch()({
        type: ActionType.OPEN_GENERATED_AWARD_POPUP,
        coordinates: { x: data.x, y: data.y },
      });
    } else {
      getGlobalDispatch()({
        type: ActionType.ENQUEUE_GENERATED_AWARD_POPUP,
        coordinates: { x: data.x, y: data.y },
      });
    }
  }
);

//
// GENERATION FAILED
//
socket.on(
  "generation failed",
  (data: { x: number; y: number; error: string; playerUID: string }) => {
    console.log("generation failed socket message");

    getGlobalDispatch()({
      type: ActionType.REMOVE_GENERATING_NOW_HEX,
      x: data.x,
      y: data.y,
    });

    getGlobalDispatch()({
      type: ActionType.CLOSE_HEX_GENERATE_POPUP,
    });

    showSystemMessage({
      systemMessage: "error",
      text: data.error,
    });
  }
);
